/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Button, IModuleProps, Modal, ModalBody, ModalHeader} from '@msdyn365-commerce-modules/utilities';
import { ICittaLightboxConfig, ICittaLightboxProps } from './citta-lightbox.props.autogenerated';

export interface ICittaLightboxViewProps extends ICittaLightboxProps<ICittaLightboxConfig> {
    CittaLighboxContainer: IModuleProps;
    LaunchLighboxButton: React.ReactNode;
    ModalPopUp: React.ReactNode;
}

interface ICittaLightBoxState extends React.ComponentState {
    isOpen: boolean;
  }

/**
 *
 * CittaLightbox component
 * @extends {React.PureComponent<ICittaLightboxProps<ICittaLightboxData>>}
 */
class CittaLightbox extends React.PureComponent<ICittaLightboxProps<ICittaLightboxConfig>, ICittaLightBoxState> {
    constructor(props: ICittaLightboxProps<ICittaLightboxConfig>) {
        super(props);
        this.state = { isOpen: false };
        this._openModal = this._openModal.bind(this);
        this._closeModal = this._closeModal.bind(this);
    }

    public componentDidMount(): void {
        const {config: { defaultOpenState } } = this.props;
        if (defaultOpenState) {
            this.setState({ isOpen: defaultOpenState});
        }
    }

    public render(): JSX.Element | null {
        const { config } = this.props;
        const launchLightboxButton = this._launchLightboxButton(config.hasLaunchLightboxButton, config.launchLightboxButtonText);
        const CittaLighboxViewProps: ICittaLightboxViewProps = {
            ...this.props,
            CittaLighboxContainer: {
                moduleProps: this.props,
                className: config.launchLightboxButtonClassName && config.launchLightboxButtonClassName !== '' ? config.launchLightboxButtonClassName : 'lightbox'
            },
            LaunchLighboxButton: launchLightboxButton,
            ModalPopUp: this._modalPopUp()
        };
        return this.props.renderView(CittaLighboxViewProps) as React.ReactElement;
    }

    private _launchLightboxButton(hasLaunchLightboxButton: boolean | undefined, buttonText: string | undefined): JSX.Element | null {
        if(hasLaunchLightboxButton) {
          return (<Button className='launch-lightbox-button' onClick={this._openModal}>{buttonText}</Button>);
        }
        return null;
    }

    private _modalPopUp(): JSX.Element | null {
        const {config} = this.props;
        return (
            <Modal
                autoFocus={config.autoFocus}
                fade={config.fade}
                isOpen={this.state.isOpen}
                horizontalPosition={config.horizontalPosition}
                verticalPosition={config.verticalPosition}
                zIndex={config.zIndex}
                toggle={this._closeModal}
                applicationNode={'rsg-root'}
                className = {config.lightboxClassName}
                modalTransition={{
                    onMouseDown :(e)=> {e.preventDefault();}
                }}
            >
                <ModalHeader toggle={this._closeModal}>{config.showModalHeader ? config.modalHeader: null}</ModalHeader>
                <ModalBody>
                {this.props.slots &&
                this.props.slots.content &&
                this.props.slots.content.length
                    ? this.props.slots.content.map(
                        (slot: React.ReactNode, index: number) => {
                        return <div key={index}>{slot}</div>;
                        }
                    )
                    : false}
                </ModalBody>
            </Modal>
        );
    }

    private _closeModal(): void {
        this.setState(prevState => ({
          isOpen: !prevState.isOpen
        }));
    }

    private _openModal(): void {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen
        }));
    }
}

export default CittaLightbox;
